import React from "react";
import { Link } from "react-router-dom";
import { photoUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { Calendar, MapPin, Building2, ShieldCheck } from "lucide-react";

const FALLBACK =
  "https://images.unsplash.com/photo-1523217582562-09d0def993a6?crop=entropy&cs=srgb&fm=jpg&w=940&q=80";

export default function ProjectCard({ project, index = 0 }) {
  const { t, formatPrice } = useLocale();
  const first = project.photos?.[0];
  const src = first ? photoUrl(first) : FALLBACK;
  const monthLabel = t("projects.months")[project.delivery_month - 1];

  return (
    <Link
      to={`/projects/${project.project_id}`}
      data-testid={`project-card-${project.project_id}`}
      className="prop-card group block animate-fade-up"
      style={{ animationDelay: `${(index % 6) * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded-sm">
        <img src={src} alt={project.name} loading="lazy" className="prop-card-img h-full w-full object-cover" />
        <div className="absolute top-3 left-3 label-eyebrow bg-background/90 backdrop-blur px-2 py-1 rounded-sm text-primary">
          {t("projects.title")}
        </div>
        {project.confotur && (
          <div
            className="absolute top-3 right-3 inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-semibold"
            data-testid={`confotur-badge-${project.project_id}`}
          >
            <ShieldCheck className="h-3 w-3" /> {t("projects.confotur")}
          </div>
        )}
      </div>
      <div className="pt-4 pb-2 space-y-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" />
          <span className="truncate">{project.developer}</span>
        </div>
        <h3 className="font-serif text-2xl leading-tight group-hover:text-primary transition-colors">
          {project.name}
        </h3>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span className="truncate">{project.address}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-foreground/80">
          <Calendar className="h-3.5 w-3.5 text-primary" />
          <span>
            {monthLabel} {project.delivery_year}
          </span>
        </div>
        <div className="pt-2">
          <div className="label-eyebrow">{t("projects.price_range")}</div>
          <div className="font-serif text-xl text-primary mt-0.5">
            {formatPrice(project.price_min, project.currency)}
            <span className="text-muted-foreground text-base mx-1">–</span>
            {formatPrice(project.price_max, project.currency)}
          </div>
        </div>
      </div>
    </Link>
  );
}
